import { shouldShowPaymentSection, invoiceFieldsFor } from './draw-quote.mjs';

/**
 * 付款區共用 DOM 層：金流清單、發票型別／欄位、通知信箱、付款選擇的保存與還原、顯隱切換。
 * 只操作這八個 id：`#wtoy_draw_pay_gateways`、`_gateway_list`、`_offline_notice`、`_card`、
 * `_invoice`、`_invoice_types`、`_invoice_fields`（皆 `#wtoy_draw_pay_*` 前綴）與
 * `#wtoy_draw_pay_email_input`（通知信箱欄，id 不循同一個前綴規律）。
 * 不認呼叫端是抽獎、轉蛋還是出貨（kind 中性）。
 *
 * 不碰 `#wtoy_draw_pay_confirm`、`#wtoy_draw_pay_block_reason`、
 * `#wtoy_draw_pay_total_amount`、`#wtoy_draw_pay_product_*`——那些留在各自呼叫端。
 * 不碰 TapPay SDK（`setupTapPayCardFields()` 在 `tappay-card.mjs`，抽獎與出貨共用）。
 *
 * `.js` 不是 `.mjs`：含 DOM，不納入 `node --test`（既有決議 18）。
 *
 * @param {object} deps
 * @param {object} deps.$ jQuery
 * @param {(lockSeconds: number) => string} deps.offlineNotice 離線付款提示文案，
 *   抽獎／出貨各傳各的（`offlineNoticeText` / `shipOfflineNoticeText`）
 */
export function createPaySection(deps) {
    var $ = deps.$;

    var INVOICE_FIELD_LABELS = {
        buyer_name: '買受人',
        tax_number: '統一編號',
        title: '公司抬頭',
        carruer_num: '載具號碼',
        lovecode: '捐贈碼',
    };

    // 買受人不進 PayPreference（回寫的是 billing_first_name，見脈絡 §七），
    // 預填改由呼叫端以 quote 的 buyer_name 呼叫 setBuyerName() 帶入
    var buyerName = '';

    function setBuyerName(value) {
        buyerName = value || '';
    }

    // 無原型的查表：gw.icon 直接當 key 查，繼承屬性（constructor / toString）不該命中
    var GATEWAY_ICONS = Object.assign(Object.create(null), {
        card: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>',
        bank: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><line x1="3" y1="21" x2="21" y2="21"/><line x1="5" y1="21" x2="5" y2="10"/><line x1="19" y1="21" x2="19" y2="10"/><polygon points="12,3 21,9 3,9"/></svg>',
        store: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 9l1-5h16l1 5"/><path d="M4 9v11h16V9"/><path d="M9 20v-6h6v6"/></svg>',
        qr: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><line x1="14" y1="14" x2="21" y2="14"/><line x1="14" y1="21" x2="21" y2="21"/><line x1="17" y1="14" x2="17" y2="21"/></svg>',
        wallet: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/><path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/><path d="M18 12a2 2 0 0 0 0 4h3v-4z"/></svg>',
    });

    function escHtml(str) {
        return String(str).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
    }

    function renderGateways(gateways) {
        $('#wtoy_draw_pay_offline_notice').text('').prop('hidden', true);
        $('#wtoy_draw_pay_gateway_list').html(gateways.map(function(gw) {
            var icon = gw.icon_url
                ? '<img src="' + escHtml(gw.icon_url) + '" alt="">'
                : (GATEWAY_ICONS[gw.icon] || GATEWAY_ICONS.wallet);
            return '<label class="wtoy-draw-pay-gateway-option">' +
                '<input type="radio" name="wtoy_draw_pay_gateway" value="' + escHtml(gw.id) +
                '" data-redirect-mode="' + escHtml(gw.redirect_mode) +
                '" data-offline="' + (gw.offline ? '1' : '0') +
                '" data-lock-seconds="' + escHtml(gw.lock_seconds) + '">' +
                '<span class="wtoy-draw-pay-option-label">' +
                '<span class="wtoy-draw-pay-option-icon">' + icon + '</span>' +
                '<span>' + escHtml(gw.title) + '</span></span></label>';
        }).join(''));

        $(document.body).off('change.wtoyDrawGateway').on('change.wtoyDrawGateway', 'input[name=wtoy_draw_pay_gateway]', function() {
            var isCard = $(this).data('redirect-mode') === 'in_popup';
            $('#wtoy_draw_pay_card').prop('hidden', !isCard);

            var isOffline = $(this).data('offline') === 1;
            var noticeText = isOffline ? deps.offlineNotice(Number($(this).data('lock-seconds')) || 0) : '';
            $('#wtoy_draw_pay_offline_notice').text(noticeText).prop('hidden', noticeText === '');
        });
    }

    function renderInvoiceFields(type, donationGroups) {
        var fields = invoiceFieldsFor(type);
        var html = fields.filter(function(field) {
            return field !== 'lovecode';
        }).map(function(field) {
            // 買受人渲染當下就得帶 value：applyPreference() 會先 trigger('change') 讓欄位
            // 整份重建，重建後才按 name 填偏好值，買受人不在偏好裡，渲染後才填會被這次重建洗掉
            var valueAttr = field === 'buyer_name' ? ' value="' + escHtml(buyerName) + '"' : '';
            var maxLengthAttr = field === 'buyer_name' ? ' maxlength="60"' : '';
            return '<label class="wtoy-draw-pay-invoice-field">' +
                '<span>' + escHtml(INVOICE_FIELD_LABELS[field] || field) + '</span>' +
                '<input type="text" name="wtoy_draw_pay_invoice_' + escHtml(field) + '"' + valueAttr + maxLengthAttr + '>' +
                '</label>';
        }).join('');

        if (type === 'donation') {
            html += '<select name="wtoy_draw_pay_invoice_lovecode">' +
                '<option value="">請選擇捐贈團體</option>' +
                Object.keys(donationGroups || {}).map(function(code) {
                    return '<option value="' + escHtml(code) + '">' + escHtml(donationGroups[code]) + '</option>';
                }).join('') +
                '</select>';
        }

        $('#wtoy_draw_pay_invoice_fields').html(html);
    }

    function renderInvoiceTypes(invoiceTypes, donationGroups) {
        var typeLabels = { individual: '二聯式（個人）', company: '三聯式（公司）', phone_carruer: '手機條碼', id_carruer: '自然人憑證', donation: '捐贈' };
        // 欄位區會被搬到所選那列底下，重建清單前先移回原位，否則會被 html() 一併清掉
        $('#wtoy_draw_pay_invoice_fields').appendTo('#wtoy_draw_pay_invoice').empty();
        $('#wtoy_draw_pay_invoice_types').html(invoiceTypes.map(function(type) {
            return '<label class="wtoy-draw-pay-invoice-type-option">' +
                '<input type="radio" name="wtoy_draw_pay_invoice_type" value="' + escHtml(type) + '">' +
                '<span>' + escHtml(typeLabels[type] || type) + '</span></label>';
        }).join(''));

        $(document.body).off('change.wtoyInvoiceType').on('change.wtoyInvoiceType', 'input[name=wtoy_draw_pay_invoice_type]', function() {
            // 切換型別會整份重建欄位，買受人不像其他欄位有 restorePaymentChoice() 接住，
            // 重建前先把使用者已輸入的值餵回 buyerName，重建時才不會被 quote 帶來的舊值蓋掉
            var typedBuyerName = $('#wtoy_draw_pay_invoice_fields [name=wtoy_draw_pay_invoice_buyer_name]').val();
            if (typedBuyerName !== undefined) {
                setBuyerName(typedBuyerName);
            }
            renderInvoiceFields($(this).val(), donationGroups);
            // 載具號碼、捐贈碼這些欄位要緊貼所選的那一列，不要落在整份清單最後
            $('#wtoy_draw_pay_invoice_fields').insertAfter($(this).closest('.wtoy-draw-pay-invoice-type-option'));
        });
    }

    /** 重繪前先記下使用者的付款選擇與已填欄位（欄位名保留 DOM 上的原名，供寫回） */
    function capturePaymentChoice() {
        var invoiceFields = {};
        $('#wtoy_draw_pay_invoice_fields [name]').each(function() {
            invoiceFields[$(this).attr('name')] = $(this).val();
        });
        return {
            gateway: $('input[name=wtoy_draw_pay_gateway]:checked').val() || '',
            invoiceType: $('input[name=wtoy_draw_pay_invoice_type]:checked').val() || '',
            invoiceFields: invoiceFields,
        };
    }

    /** 重繪後把選擇寫回。change 要真的觸發——信用卡欄位、載具欄位都掛在它底下 */
    function restorePaymentChoice(picked) {
        if (picked.gateway) {
            $('input[name=wtoy_draw_pay_gateway][value="' + picked.gateway + '"]')
                .prop('checked', true).trigger('change');
        }
        if (!picked.invoiceType) {
            return;
        }
        $('input[name=wtoy_draw_pay_invoice_type][value="' + picked.invoiceType + '"]')
            .prop('checked', true).trigger('change');
        Object.keys(picked.invoiceFields).forEach(function(name) {
            $('#wtoy_draw_pay_invoice_fields [name="' + name + '"]').val(picked.invoiceFields[name]);
        });
    }

    /**
     * 套用跨次記憶的付款偏好（僅彈窗本次開啟後的第一次渲染呼叫一次，見呼叫端的旗標）。
     * 鍵名對映同 {@see collectInvoiceFields} 的反向版：後端鍵 `billing_owp_wcaei_*`
     * 換回 DOM 上的 `wtoy_draw_pay_invoice_*`。落空的項目（後端已依本次可用清單濾過）
     * 一律跳過，不預選任何替代值。
     */
    function applyPreference(pref) {
        if (!pref) {
            return;
        }
        if (pref.gateway_id) {
            $('input[name=wtoy_draw_pay_gateway][value="' + pref.gateway_id + '"]')
                .prop('checked', true).trigger('change');
        }
        if (!pref.invoice_type) {
            return;
        }
        $('input[name=wtoy_draw_pay_invoice_type][value="' + pref.invoice_type + '"]')
            .prop('checked', true).trigger('change');
        Object.keys(pref.invoice_fields || {}).forEach(function(key) {
            var domName = key.replace('billing_owp_wcaei_', 'wtoy_draw_pay_invoice_');
            $('#wtoy_draw_pay_invoice_fields [name="' + domName + '"]').val(pref.invoice_fields[key]);
        });
    }

    function collectInvoiceFields() {
        var data = {};
        $('#wtoy_draw_pay_invoice_fields [name]').each(function() {
            var $field = $(this);
            data[$field.attr('name').replace('wtoy_draw_pay_invoice_', 'billing_owp_wcaei_')] = $field.val();
        });
        return data;
    }

    /** gateways / invoice / card 三塊的顯隱，依 quote 是否該展開付款區決定。 */
    function applyVisibility(quote) {
        var showPayment = shouldShowPaymentSection(quote);
        $('#wtoy_draw_pay_gateways, #wtoy_draw_pay_invoice').prop('hidden', !showPayment);
        // 信用卡欄位是 gateways 的兄弟節點、平時只由 gateway 的 change 切顯示，
        // 現金差額歸零時得在這裡一起收掉，否則勾回點數後 TapPay iframe 會留在畫面上
        if (!showPayment) {
            $('#wtoy_draw_pay_card').prop('hidden', true);
        }
        return showPayment;
    }

    /** 通知信箱欄目前的值，去頭尾空白。 */
    function notifyEmail() {
        return ($('#wtoy_draw_pay_email_input').val() || '').trim();
    }

    /** 目前選中的付款方式（讀 DOM 上的 radio 與其 data 屬性，未選回 null）。 */
    function selectedGateway() {
        var $checked = $('input[name=wtoy_draw_pay_gateway]:checked');
        if (!$checked.length) {
            return null;
        }
        return {
            id: $checked.val(),
            redirect_mode: $checked.data('redirect-mode'),
            offline: $checked.data('offline') === 1,
            lock_seconds: Number($checked.data('lock-seconds')) || 0,
        };
    }

    return {
        renderGateways: renderGateways,
        renderInvoiceTypes: renderInvoiceTypes,
        renderInvoiceFields: renderInvoiceFields,
        capturePaymentChoice: capturePaymentChoice,
        restorePaymentChoice: restorePaymentChoice,
        applyPreference: applyPreference,
        collectInvoiceFields: collectInvoiceFields,
        applyVisibility: applyVisibility,
        selectedGateway: selectedGateway,
        setBuyerName: setBuyerName,
        notifyEmail: notifyEmail,
    };
}
